import { FC, memo, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Select from '../atom/Select';
import { RotaryHtexSectionFieldsProps } from '../../models';
import type { IState } from '../../../../models';
import {
  useGetSeriesIdsByMfgIdQuery,
  useGetTypeIdsByMfgIdAndSeriesIdQuery,
  useGetRotorStylesByMfgIdAndSeriesIdQuery,
} from '../../services/erWheelApi';

/**
 * Ensures select value always exists and is valid
 */
const ensureValidSelectValue = <T extends { id: number }>(
  currentValue: number | undefined,
  options: T[] | undefined,
  defaultVal?: number,
): number | undefined => {
  if (!options || options.length === 0) return undefined;

  if (!currentValue)
    return !defaultVal
      ? options[0].id
      : (options.find((o) => o.id === defaultVal) || options[0])?.id;

  const exists = options.some((opt) => opt.id === currentValue);
  return exists
    ? currentValue
    : !defaultVal
      ? options[0].id
      : (options.find((o) => o.id === defaultVal) || options[0])?.id;
};

const RotaryHtexSectionFields: FC<RotaryHtexSectionFieldsProps> = ({
  section,
  onFieldChange,
}) => {
  const params = useSelector((state: IState) => state.erWheel.er_wheel_params);
  const ahu_params = useSelector((state: IState) => state.ahu.ahu_params);

  /** -------------------------
   * Queries (clean dependencies)
   * ------------------------ */

  const { data: seriesIds } = useGetSeriesIdsByMfgIdQuery(section.mfg_id!, {
    skip: !section.mfg_id,
  });

  const { data: typeIds } = useGetTypeIdsByMfgIdAndSeriesIdQuery(
    {
      mfg_id: section.mfg_id!,
      series_id: section.series_id!,
    },
    { skip: !section.mfg_id || !section.series_id },
  );

  const { data: rotorStyles } = useGetRotorStylesByMfgIdAndSeriesIdQuery(
    {
      mfg_id: section.mfg_id!,
      series_id: section.series_id!,
    },
    { skip: !section.mfg_id || !section.series_id },
  );

  /** -------------------------
   * Filtered options
   * ------------------------ */

  const filteredSeries = useMemo(() => {
    if (!params?.series || !seriesIds) return [];
    return params.series.filter((s) => seriesIds.includes(s.id));
  }, [params?.series, seriesIds]);

  /** -------------------------
   * Reset cascade
   * ------------------------ */

  // mfg → reset everything below
  useEffect(() => {
    if (!section.mfg_id) return;

    onFieldChange(undefined, 'series_id');
    onFieldChange(undefined, 'type_id');
    onFieldChange(undefined, 'rotor_style_id');
  }, [section.mfg_id]);

  // series → reset types + rotor style
  useEffect(() => {
    if (!section.series_id) return;

    onFieldChange(undefined, 'type_id');
    onFieldChange(undefined, 'rotor_style_id');
  }, [section.series_id]);

  /** -------------------------
   * Auto-select first valid option
   * ------------------------ */

  // Manufacturer
  useEffect(() => {
    const next = ensureValidSelectValue(section.mfg_id, params?.manufacturer.filter((e) => e.id !== 237));
    if (next && next !== section.mfg_id) {
      onFieldChange(next, 'mfg_id');
    }
  }, [params?.manufacturer]);

  // Series
  useEffect(() => {
    if (!filteredSeries) return;
    const next = ensureValidSelectValue(section.series_id, filteredSeries, 828);

    if (next && next !== section.series_id) {
      onFieldChange(next, 'series_id');
    }
  }, [filteredSeries]);

  // Type
  useEffect(() => {
    if (!typeIds) return;
    const next = ensureValidSelectValue(section.type_id, typeIds);

    if (next && next !== section.type_id) {
      onFieldChange(next, 'type_id');
    }
  }, [typeIds]);

  // Rotor style
  useEffect(() => {
    if (!rotorStyles) return;
    const next = ensureValidSelectValue(
      section.rotor_style_id,
      rotorStyles ?? params?.rotor_style,
    );
    if (next && next !== section.rotor_style_id) {
      onFieldChange(next, 'rotor_style_id');
    }
  }, [rotorStyles, params?.rotor_style]);

  /** -------------------------
   * Render
   * ------------------------ */

  return (
    <div className="bot-section__field-group">
      <div className="bot-section__field">
        <span className="text-span">with</span>
        <Select
          name="mfg_id"
          value={section.mfg_id ?? ''}
          options={params?.manufacturer.filter((m) => m.id !== 237)}
          onChange={onFieldChange}
          className="bot-section__select"
          border_bottom={false}
        />
      </div>

      <div className="bot-section__field">
        <span className="text-span with-padding-left">series</span>
        <Select
          name="series_id"
          value={section.series_id ?? ''}
          options={filteredSeries}
          onChange={onFieldChange}
          className="bot-section__select"
          border_bottom={false}
        />
        <span className="text-span with-padding-right with-comma">,</span>
      </div>

      <div className="bot-section__field">
        <Select
          name="type_id"
          value={section.type_id ?? ''}
          options={typeIds}
          onChange={onFieldChange}
          className="bot-section__select"
          isLowerCase
          border_bottom={false}
        />
        <span className="text-span with-padding-right with-comma">,</span>
      </div>

      <div className="bot-section__field">
        <Select
          name="rotor_style_id"
          value={section.rotor_style_id ?? ''}
          options={rotorStyles ?? params?.rotor_style ?? []}
          onChange={onFieldChange}
          className="bot-section__select"
          isLowerCase
          border_bottom={false}
        />
        <span className="text-span with-padding-right">wheel, with</span>
      </div>
      <div className="bot-section__field">
        <Select
          name="air_stream_id"
          value={section.air_stream_id ?? ''}
          options={ahu_params?.air.air_stream || []}
          onChange={onFieldChange}
          className="bot-section__select"
          isLowerCase
          border_bottom={false}
          disabled
        />
        <span className="text-span with-padding-right">wheel, with</span>
      </div>
    </div>
  );
};

export default memo(RotaryHtexSectionFields);
